import { useQueryClient } from '@tanstack/react-query';
import {
  useAdminDeleteProductTrial,
  getAdminGetProductTrialsQueryKey,
} from '@/api/auth/admin/admin';
import { getAdminGetProductsQueryKey } from '@/api/auth/admin/admin';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { logger } from '@/services/logger';

/**
 * Props for the DeleteTrialDialog component
 */
interface DeleteTrialDialogProps {
  /** The product ID (for query invalidation) */
  productId: string;
  /** The trial ID to delete */
  trialId: string | null;
  /** The trial token to display in confirmation */
  trialToken?: string;
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog open state changes */
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog component for confirming product trial deletion
 */
export function DeleteTrialDialog({
  productId,
  trialId,
  trialToken,
  open,
  onOpenChange,
}: DeleteTrialDialogProps) {
  const queryClient = useQueryClient();

  const { mutate: deleteTrial, isPending: isDeletingTrial } = useAdminDeleteProductTrial({
    mutation: {
      onSuccess: () => {
        toast.success('Trial deleted successfully');
        // Invalidate queries to refresh the lists
        queryClient.invalidateQueries({
          queryKey: getAdminGetProductTrialsQueryKey(productId),
        });
        queryClient.invalidateQueries({
          queryKey: getAdminGetProductsQueryKey(),
        });
        // Close dialog
        onOpenChange(false);
      },
      onError: (error: any) => {
        logger.error(error, {
          context: 'DeleteTrial',
          productId,
          trialId,
        });
        toast.error('Failed to delete trial. ' + (error.response?.data?.detail || error.message));
      },
    },
  });

  /**
   * Handle trial deletion
   */
  const handleDelete = () => {
    if (trialId) {
      deleteTrial({ trialId });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Delete Trial</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this trial? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-muted-foreground">Trial Token:</p>
          <code className="relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm">
            {trialToken || 'N/A'}
          </code>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeletingTrial}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeletingTrial}
          >
            {isDeletingTrial ? 'Deleting...' : 'Delete Trial'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
